import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import {
  Subject,
  EMPTY,
  of,
  takeUntil,
  catchError,
  switchMap,
  finalize,
} from 'rxjs';
import { BroadcastService } from '../../features/broadcast-config/broadcast.service';
import { CommonService } from '../../shared/services/common.service';
import { NotificationService } from './notification.service';
import { NotificationFilterType } from '../models/notification.model';
import { AppService } from './app.service';
import { AuthService } from './auth.service';
import { userDetailModel } from '../../features/organizations/organization.interface';

@Injectable()
export class BroadcastNotificationService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly excludedRoutes = [
    '/login',
    '/verify-user',
    '/forgot-password',
    '/reset-password',
    '/select-organization',
    '/broadcast-config',
  ];
  private isFetchingBroadcasts = false;
  private readonly processedAlertIds = new Set<number>();
  private currentRoute = '';
  private organizationId: number | null = null;
  private userData: userDetailModel | null = null;

  constructor(
    private readonly broadcastService: BroadcastService,
    private readonly commonService: CommonService,
    private readonly notificationService: NotificationService,
    private readonly router: Router,
    private readonly appService: AppService,
    private readonly authService: AuthService
  ) {
    this.appService.notificationReceived$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ body }) => {
        this.handleNotification(body);
      });
    this.authService.userData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.userData = user;
      });
  }

  ngOnDestroy(): void {
    this.clearAllNotificationStates();
    this.destroy$.next();
    this.destroy$.complete();
  }

  setCurrentRoute(route: string): void {
    this.currentRoute = route;
  }

  setOrganizationId(orgId: number | null): void {
    this.organizationId = orgId;
    this.appService.setCurrentOrganization(orgId);
  }

  isExcludedRoute(url: string): boolean {
    return this.excludedRoutes.some(route => url.startsWith(route));
  }

  clearAllNotificationStates(): void {
    this.commonService.clearAllNotifications();
    this.broadcastService.clearShownBroadcasts();
    this.processedAlertIds.clear();
  }

  setupBroadcastNotifications(): void {
    if (this.isExcludedRoute(this.currentRoute)) {
      return;
    }

    this.broadcastService.alerts$
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('Error fetching alerts:', error);
          return EMPTY;
        })
      )
      .subscribe(activeAlerts => {
        if (activeAlerts.length === 0) {
          return;
        }

        if (
          activeAlerts.length > 0 &&
          !this.isExcludedRoute(this.currentRoute)
        ) {
          const broadcastIds: number[] = [];
          const newAlerts = activeAlerts.filter(
            alert => !this.processedAlertIds.has(alert.id)
          );

          if (newAlerts.length === 0) {
            return;
          }

          newAlerts.forEach(alert => {
            this.processedAlertIds.add(alert.id);
            broadcastIds.push(alert.id);
          });

          if (broadcastIds.length > 0) {
            this.broadcastService.markBroadcastsAsShown(broadcastIds);
          }
        }
      });
  }

  fetchBroadcastData(organizationId: number | null): void {
    if (this.isExcludedRoute(this.currentRoute) || this.isFetchingBroadcasts) {
      return;
    }

    this.isFetchingBroadcasts = true;

    of(organizationId)
      .pipe(
        switchMap(orgId => {
          return this.broadcastService.getBroadcasts(orgId || undefined).pipe(
            catchError(error => {
              console.error('Error fetching broadcasts:', error);
              return EMPTY;
            }),
            finalize(() => {
              this.isFetchingBroadcasts = false;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        setTimeout(() => {
          this.setupBroadcastNotifications();
        }, 300);
      });
  }

  refreshBroadcastsWithClearing(organizationId: number | null): void {
    this.clearAllNotificationStates();
    this.fetchBroadcastData(organizationId);
  }

  makeApiCallsForNotifications(organizationId: number | null): void {
    this.notificationService
      .getNotificationsByUserId(NotificationFilterType.All, organizationId)
      .subscribe({
        next: response => {
          if (response?.result) {
            this.notificationService.combineWithBroadcastNotifications(
              response.result
            );
          }
        },
        error: error => {
          console.error('Error loading notifications:', error);
        },
      });
    if (!this.userData?.isSuperAdmin) {
      this.broadcastService
        .getBroadcastNotificationsByOrgId(organizationId)
        .subscribe({
          next: response => {
            if (response?.result) {
              this.notificationService.combineWithBroadcastNotifications(
                response.result
              );
            }
          },
          error: error => {
            console.error('Error loading broadcast notifications:', error);
          },
        });
    }
  }

  handleNotification(notificationBody: string): void {
    try {
      const notificationData = JSON.parse(notificationBody);
      const isOnNotificationsPage = this.router.url.includes('/notifications');
      this.makeApiCallsForNotifications(this.organizationId);

      if (notificationData.IsBroadcast === true || isOnNotificationsPage) {
        this.refreshBroadcastsWithClearing(this.organizationId);
      }
    } catch (error) {
      console.error('Error parsing broadcast notification:', error);
      this.makeApiCallsForNotifications(this.organizationId);

      if (this.router.url.includes('/notifications')) {
        this.refreshBroadcastsWithClearing(this.organizationId);
      }
    }
  }
}
